import { AnyAction, ThunkDispatch, createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { logger, monitoring } from 'common/services';
import { isError } from 'common/utils';
import type { MediaDevice, MediaSettingsState } from 'devices/types';
import { getMediaDevices, queryBrowserPermission, tryGetUserMedia } from 'devices/utils';
import { isEqual } from 'lodash-es';
import { addAppErrorMessage } from 'modules/alerts/state';
import type { AppDispatch, RootState } from 'store';

import { selectCameraPermissionState, selectMediaDevices, selectMicrophonePermissionState } from './selectors';
import { UserSettings } from './types';
import { setCameraPermissionState, setMicrophonePermissionState } from './userSlice.actions';

type ThunkAPI = {
  dispatch: AppDispatch;
  state: RootState;
  extra: unknown;
  rejectWithValue: unknown;
};

type DevicesChangedOptions = {
  audioOnly?: boolean;
};

/**
 * Pure resolver — determines the actual permission state for a device kind.
 * Only called when permission state is 'unknown' (first load).
 * Never triggers a browser prompt. Uses Permissions API when available,
 * falls back to enumerateDevices heuristic for Safari < 16.
 */
const resolveDevicePermission = async (
  kind: 'microphone' | 'camera'
): Promise<'granted' | 'denied' | 'system_denied' | 'prompt'> => {
  const browserState = await queryBrowserPermission(kind);

  switch (browserState) {
    case 'granted': {
      // Browser says granted. Only verify OS-level access (which won't prompt) when a
      // device of this kind actually exists — otherwise getUserMedia fails with
      // NotFoundError purely because there's no hardware, and we'd mislabel a genuinely
      // granted permission as system_denied. With permission already held, the device
      // works silently once connected, so trust the grant.
      const deviceKind = kind === 'microphone' ? 'audioinput' : 'videoinput';
      const devices = await getMediaDevices();
      const hasDeviceOfKind = devices.some((d) => d.kind === deviceKind);
      if (!hasDeviceOfKind) {
        return 'granted';
      }

      const hasAccess = await tryGetUserMedia(kind);
      if (!hasAccess) {
        logger.warn(`[resolveDevicePermission] ${kind} denied at OS level`);
        monitoring.trackAction(`devices.${kind}.system_permission_denied`);
      }
      return hasAccess ? 'granted' : 'system_denied';
    }

    case 'denied':
      return 'denied';

    case 'prompt':
      return 'prompt';

    default: {
      // Permissions API unavailable (Safari < 16)
      const deviceKind = kind === 'microphone' ? 'audioinput' : 'videoinput';
      const devices = await getMediaDevices();
      const hasLabeledDevices = devices.some((d) => d.kind === deviceKind && d.label);

      if (hasLabeledDevices) {
        // Labels visible = browser cached permission = getUserMedia won't prompt
        const hasAccess = await tryGetUserMedia(kind);
        if (!hasAccess) {
          logger.warn(`[resolveDevicePermission] ${kind} denied (Permissions API unavailable)`);
          monitoring.trackAction(`devices.${kind}.permission_denied_on_verification`);
        }
        return hasAccess ? 'granted' : 'system_denied';
      }

      // No labeled devices and no Permissions API — can't determine state without prompting
      return 'prompt';
    }
  }
};

// Dedup guards: prevent double resolveDevicePermission calls when two rapid
// devicesChanged dispatches (e.g. initial load + immediate devicechange event)
// both read 'unknown' from a stale state snapshot. The second call awaits the
// first's promise instead of opening hardware a second time. Module-level
// because the race spans separate async thunk invocations that share no closure.
let micPermissionResolving: Promise<'granted' | 'denied' | 'system_denied' | 'prompt'> | null = null;
let camPermissionResolving: Promise<'granted' | 'denied' | 'system_denied' | 'prompt'> | null = null;

/**
 * Called in multiple contexts:
 * - On initial page load (via useDevicesChangeListener / useAudioDevicesChangeListener)
 * - On every browser 'devicechange' event (e.g. USB mic plugged/unplugged)
 * - Every 3 seconds on Android Chrome (via usePollForDeviceListChanges)
 *
 * Permission resolution only runs once (when state is 'unknown').
 * All subsequent calls just re-enumerate devices.
 */
const getDevices = async (
  state: RootState,
  audioOnly: boolean,
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>
): Promise<MediaDevice[]> => {
  // Resolve permissions once on first load
  if (selectMicrophonePermissionState(state) === 'unknown') {
    if (!micPermissionResolving) {
      micPermissionResolving = resolveDevicePermission('microphone');
    }
    try {
      dispatch(setMicrophonePermissionState(await micPermissionResolving));
    } finally {
      micPermissionResolving = null;
    }
  }

  if (!audioOnly && selectCameraPermissionState(state) === 'unknown') {
    if (!camPermissionResolving) {
      camPermissionResolving = resolveDevicePermission('camera');
    }
    try {
      dispatch(setCameraPermissionState(await camPermissionResolving));
    } finally {
      camPermissionResolving = null;
    }
  }

  return getMediaDevices();
};

const devicesChangedHandler = async (
  options: DevicesChangedOptions | undefined,
  { dispatch, getState }: { dispatch: ThunkDispatch<RootState, unknown, AnyAction>; getState: () => RootState }
): Promise<void> => {
  try {
    const state = getState();
    const newDevices: MediaDevice[] = await getDevices(state, options?.audioOnly ?? false, dispatch);

    const currentMediaDeviceIds = selectMediaDevices(state).map(({ deviceId }) => deviceId);
    const newDeviceIds = newDevices.map(({ deviceId }) => deviceId);
    // We do this comparison to prevent unnecessary re-renders
    if (!isEqual(currentMediaDeviceIds, newDeviceIds)) {
      dispatch(userActions.navigator.mediaDevices.devicesFound(newDevices));
    }
  } catch (error) {
    logger.error(error);

    if (isError(error)) {
      dispatch(addAppErrorMessage(error.message));
    }
  }
};

export const userActions = {
  navigator: {
    mediaDevices: {
      devicesChanged: createAsyncThunk<void, DevicesChangedOptions | undefined, ThunkAPI>(
        '[navigator mediaDevices] devices changed',
        devicesChangedHandler
      ),
      pollForDevicesListChange: createAsyncThunk<void, DevicesChangedOptions | undefined, ThunkAPI>(
        '[navigator mediaDevices] poll for devices list change',
        devicesChangedHandler
      ),
      devicesFound: createAction<MediaDevice[]>('[navigator mediaDevices] devices found'),
    },
  },
  mediaSettings: {
    settingsUpdated: createAction<UserSettings & MediaSettingsState>('[mediaSettings] settings updated'),
  },
};

// Device acquisition now flows through `acquireDevicesThunk` + the `devices/core` acquire
// primitive, which classifies every failure (including OS-block detection) via the shared
// mapper. See ADR 0001 (apps/frontend/docs/adr).
